/**
 * Gallery Page - Main page displaying simulation previews
 * Requirements: 1.1, 1.2, 1.3
 */

import type { SimulationConfig } from '@langton/shared';
import type { SerializedState } from '@langton/shared';
import { SimulationPreview } from '../components/simulation-preview.js';
import { MapPreview, parseMapsResponse } from '../components/map-preview.js';
import { WebSocketClient } from '../websocket/websocket-client.js';
import { MusicPlayer } from '../components/music-player.js';

/**
 * GalleryPage - Main gallery page component
 * 
 * Requirements:
 * - 1.1: Black background with white "mepopohon.ru" header
 * - 1.2: Two-column grid layout for previews
 * - 1.3: Live preview centered on ant position
 */
export class GalleryPage {
  private container: HTMLElement;
  private wsClient: WebSocketClient;
  private previews: Map<string, SimulationPreview> = new Map();
  private mapPreviews: Map<string, MapPreview> = new Map();
  private musicPlayer: MusicPlayer;
  private galleryMusicUrl?: string;

  constructor(container: HTMLElement, wsUrl: string, galleryMusicUrl?: string) {
    this.container = container;
    this.wsClient = new WebSocketClient(wsUrl);
    this.galleryMusicUrl = galleryMusicUrl;
    this.musicPlayer = new MusicPlayer();
    this.setupUI();
    this.setupWebSocket();
  }

  /**
   * Set up the gallery UI structure
   * Requirements: 1.1, 1.2, 2.1, 2.2, 2.3, 2.6
   */
  private setupUI(): void {
    // Clear container
    this.container.innerHTML = '';
    
    // Apply black background (Req 1.1)
    this.container.style.cssText = `
      background-color: #000000;
      min-height: 100vh;
      padding: 20px;
      box-sizing: border-box;
    `;

    // Create header (Req 1.1, 2.6)
    const header = document.createElement('header');
    header.style.cssText = `
      text-align: center;
      margin-bottom: 40px;
    `;

    // Load date-specific logo or fallback to text (Req 2.1, 2.2, 2.3)
    this.loadDateLogo(header);

    // Create grid container (Req 1.2)
    const gridContainer = document.createElement('div');
    gridContainer.id = 'gallery-grid';
    gridContainer.style.cssText = `
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 20px;
      max-width: 95%;
      margin: 0 auto;
      padding: 0 20px;
    `;

    this.container.appendChild(header);
    this.container.appendChild(gridContainer);

    // Add music player button if music is configured (Req 1.6)
    if (this.galleryMusicUrl) {
      this.musicPlayer.mount(this.container, this.galleryMusicUrl);
    }
  }

  /**
   * Generate logo filename for a given date
   * Format: YYYY-MM-DD.png (Req 2.4)
   */
  static getDateLogoFilename(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}.png`;
  }

  /**
   * Load logo image for current date, fallback to text
   * Requirements: 2.1, 2.2, 2.3, 2.6
   */
  private async loadDateLogo(header: HTMLElement): Promise<void> {
    const dateStr = GalleryPage.getDateLogoFilename(new Date());
    const logoUrl = `/uploads/logos/${dateStr}`;
    
    try {
      const response = await fetch(logoUrl, { method: 'HEAD' });
      if (response.ok) {
        // Logo exists - show image (Req 2.2)
        const img = document.createElement('img');
        img.src = logoUrl;
        img.alt = 'mepopohon.ru';
        img.style.cssText = `
          max-height: 100px;
          margin: 20px 0;
        `;
        header.appendChild(img);
      } else {
        throw new Error('Logo not found');
      }
    } catch {
      // Fallback to text (Req 2.3)
      this.appendFallbackTitle(header);
    }
  }

  /**
   * Append fallback text title to header
   * Requirements: 2.3, 2.6
   */
  private appendFallbackTitle(header: HTMLElement): void {
    const title = document.createElement('h1');
    title.textContent = 'mepopohon.ru';
    title.style.cssText = `
      color: #FFFFFF;
      font-family: 'Georgia', serif;
      font-size: 3rem;
      font-weight: normal;
      letter-spacing: 0.1em;
      margin: 0;
      padding: 20px 0;
      text-shadow: 0 0 10px rgba(255, 255, 255, 0.3), 0 0 20px rgba(255, 255, 255, 0.15);
    `;
    header.appendChild(title);
  }

  /**
   * Set up WebSocket connection for live updates
   */
  private setupWebSocket(): void {
    this.wsClient.onInit((simId, state, config) => {
      this.updatePreview(simId, state, config);
    });

    this.wsClient.onSync((simId, state, config) => {
      this.updatePreview(simId, state, config);
    });

    this.wsClient.onError((simId, error) => {
      console.error(`Simulation ${simId} error:`, error);
    });

    this.wsClient.connect();
  }

  /**
   * Load simulations from API and create previews
   */
  async loadSimulations(): Promise<void> {
    try {
      const response = await fetch('/api/simulations');
      const simulations: SimulationConfig[] = await response.json();

      const gridContainer = document.getElementById('gallery-grid');
      if (!gridContainer) return;

      // Clear existing simulation previews only (map previews are managed
      // independently by loadMaps, so both can coexist in the same grid).
      this.previews.forEach(preview => {
        preview.getElement().remove();
        preview.destroy();
      });
      this.previews.clear();

      // Create preview for each visible simulation
      for (const config of simulations) {
        if (config.visible) {
          const preview = new SimulationPreview(config, (id: string) => {
            this.navigateToSimulation(id);
          });
          
          gridContainer.appendChild(preview.getElement());
          this.previews.set(config.id, preview);
          
          // Subscribe to WebSocket updates
          this.wsClient.subscribe(config.id);
        }
      }
    } catch (error) {
      console.error('Failed to load simulations:', error);
    }
  }

  /**
   * Load visible maps from the API and render their previews into the same
   * gallery grid alongside simulations.
   *
   * Requirements:
   * - 11.1: visible maps appear in the gallery as separate items
   * - 11.3: hidden maps are excluded (server-side; filtered defensively here too)
   */
  async loadMaps(): Promise<void> {
    try {
      const response = await fetch('/api/maps');
      const maps = parseMapsResponse(await response.json());

      const gridContainer = document.getElementById('gallery-grid');
      if (!gridContainer) return;

      // Clear existing map previews only (simulation previews are managed by
      // loadSimulations, so both can coexist in the same grid).
      this.mapPreviews.forEach(preview => {
        preview.getElement().remove();
        preview.destroy();
      });
      this.mapPreviews.clear();

      // Create a preview tile for each visible map (Req 11.1).
      for (const config of maps) {
        const preview = new MapPreview(config, (id: string) => {
          this.navigateToMap(id);
        });
        gridContainer.appendChild(preview.getElement());
        this.mapPreviews.set(config.id, preview);
      }
    } catch (error) {
      console.error('Failed to load maps:', error);
    }
  }

  /**
   * Update a preview with new state
   */
  private updatePreview(simId: string, serializedState: SerializedState, config: SimulationConfig): void {
    const preview = this.previews.get(simId);
    if (preview) {
      // Initialize with state (starts local simulation)
      preview.initializeWithState(serializedState, config);
    }
  }

  /**
   * Navigate to full simulation page
   */
  private navigateToSimulation(simulationId: string): void {
    window.location.href = `/simulation/${simulationId}`;
  }

  /**
   * Navigate to the interactive map view page (Req 11.2)
   */
  private navigateToMap(mapId: string): void {
    window.location.href = `/map/${mapId}`;
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.wsClient.disconnect();
    this.musicPlayer.stop();
    this.previews.forEach(preview => preview.destroy());
    this.previews.clear();
    this.mapPreviews.forEach(preview => preview.destroy());
    this.mapPreviews.clear();
  }
}
